import { PROPOSAL_PAYMENT_CONFIRM_API_ROUTE } from '../constants/api-routes';
import {
    assertErrorResponse,
    assertSuccessResponse,
} from '@modules/root/graphql/responses';
import type { ProposalPaymentConfirmOutcome } from '~/routes/remix-api.proposal-payment.confirm';

const CONFIRM_TIMEOUT_MS = 45_000;

export type ProposalPaymentConfirmResult =
    | {
          ok: true;
          outcome: ProposalPaymentConfirmOutcome;
          invoiceStatus: string;
          invoicePaymentId: number;
      }
    | { ok: false; message: string; retryable: boolean };

/**
 * Sync settlement on the server after Stripe.js reports success on the proposal page.
 * Retries once on timeout/5xx — the first request may have settled before the response arrived.
 */
export async function postProposalPaymentConfirm(params: {
    proposalUrlSegment: string;
    invoicePaymentId: string;
    intentType: 'setup' | 'payment';
    stripeIntentId: string;
}): Promise<ProposalPaymentConfirmResult> {
    const firstAttempt = await postProposalPaymentConfirmOnce(params);
    if (firstAttempt.ok || !firstAttempt.retryable) {
        return firstAttempt;
    }

    return postProposalPaymentConfirmOnce(params);
}

async function postProposalPaymentConfirmOnce(params: {
    proposalUrlSegment: string;
    invoicePaymentId: string;
    intentType: 'setup' | 'payment';
    stripeIntentId: string;
}): Promise<ProposalPaymentConfirmResult> {
    const formData = new FormData();
    formData.append('proposalUrlSegment', params.proposalUrlSegment);
    formData.append('invoicePaymentId', params.invoicePaymentId);
    formData.append('intentType', params.intentType);
    formData.append('stripeIntentId', params.stripeIntentId);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIRM_TIMEOUT_MS);

    try {
        const response = await fetch(PROPOSAL_PAYMENT_CONFIRM_API_ROUTE, {
            method: 'POST',
            body: formData,
            signal: controller.signal,
        });

        let result: unknown;
        try {
            result = await response.json();
        } catch {
            return {
                ok: false,
                message: 'Failed to confirm payment',
                retryable: response.status >= 500 || response.status === 0,
            };
        }

        if (
            result &&
            typeof result === 'object' &&
            assertSuccessResponse(result) &&
            result.data
        ) {
            const data = result.data as {
                outcome: ProposalPaymentConfirmOutcome;
                invoiceStatus: string;
                invoicePaymentId: number;
            };
            return {
                ok: true,
                outcome: data.outcome,
                invoiceStatus: data.invoiceStatus,
                invoicePaymentId: data.invoicePaymentId,
            };
        }

        const message =
            result && typeof result === 'object' && assertErrorResponse(result)
                ? String(result.message)
                : 'Failed to confirm payment';

        return {
            ok: false,
            message,
            retryable: response.status >= 500 || response.status === 0,
        };
    } catch (err) {
        const aborted = err instanceof Error && err.name === 'AbortError';
        return {
            ok: false,
            message: aborted
                ? 'Payment confirmation timed out. We will keep checking for your payment.'
                : 'Failed to confirm payment',
            retryable: true,
        };
    } finally {
        clearTimeout(timeoutId);
    }
}

/** Extract a PaymentIntent id from a Stripe client secret (`pi_…_secret_…`). */
export function paymentIntentIdFromClientSecret(
    clientSecret: string | null,
): string | null {
    if (!clientSecret) {
        return null;
    }
    const match = clientSecret.match(/^(pi_[^_]+)/);
    return match?.[1] ?? null;
}

/** Build a return URL for Stripe 3DS redirects with params used for one-shot server confirm. */
export function buildProposalStripeReturnUrl(params: {
    invoicePaymentId: string;
    intentType: 'setup' | 'payment';
}): string {
    const url = new URL(window.location.href);
    url.searchParams.set('invoice_payment_id', params.invoicePaymentId);
    url.searchParams.set('intent_type', params.intentType);
    return url.toString();
}
